import type { Reading } from "@spf/contracts";
import { toFlowModel } from "../../lib/flow.js";
import { FlowStage } from "./FlowStage.js";
import { FlowLegend } from "./FlowLegend.js";

interface Props {
  reading: Reading;
  /** Dims the picture: what it shows is no longer known to be current. */
  stale: boolean;
}

/**
 * Where the power is going, right now.
 *
 * Driven by `toFlowModel` in `lib/flow.ts`, unchanged and reused rather than
 * reimplemented. That module is where the sign conventions live -- exactly
 * zero means idle rather than "0 W discharging", an off-grid inverter never
 * exports so a non-positive grid figure is "not carrying anything" rather than
 * a reverse flow -- and all of it is already pinned by tests that render
 * nothing. The design this is restyled from carried its own hardcoded flow
 * figures instead; taking those would have re-derived by hand, and got wrong,
 * rules that were already verified.
 *
 * **It draws the design's `FlowStage`, and that is a bug fix rather than a
 * restyle.** It used to draw `FlowDiagram`, whose SVG is `w-full` with no
 * ceiling. Inside the Live view's old full-width panel that was survivable; as
 * soon as the ported page put it in a card spanning the content column it
 * scaled to about three times its intended size, with node labels at 40px and
 * arrowheads to match -- which is the same class of defect that shipped in
 * slice 4 and survived five rounds of review, because every check read text
 * and roles and none of them looked at the page. `FlowStage` is capped at
 * `max-w-[560px]`, so it cannot recur here, and the two views now draw the
 * same diagram from the same component rather than two that can drift.
 *
 * `data-stale` rather than a `flow--stale` class: the state is worth asserting
 * and a style hook is not, so the attribute says what is true and the
 * stylesheet is free to change.
 */
export function PowerFlow({ reading, stale }: Props) {
  const model = toFlowModel(reading);

  return (
    <div data-stale={stale} className={stale ? "opacity-60" : undefined}>
      <FlowStage model={model} stale={stale} />
      <FlowLegend flows={model.flows} />
    </div>
  );
}
